import {
  getCurrentClientSlug,
  getCurrentBranchSlug as getBranchSlugFromHost,
  getCurrentParentSlug as getParentSlugFromHost,
  parseHostname,
} from './subdomain';

const STATE_STORAGE_KEY = 'tenant_state';
const STATE_CODE_REGEX = /^[A-Z]{2}$/;

const normalizeStateId = (value) => {
  if (value === undefined || value === null) return null;
  const normalized = String(value).trim().toUpperCase();
  return STATE_CODE_REGEX.test(normalized) ? normalized : null;
};

export function stripStatePrefix(pathname) {
  if (!pathname) return '/';
  const match = pathname.match(/^\/[a-zA-Z]{2}(?=\/|$)/);
  if (!match) return pathname;
  const remainder = pathname.slice(match[0].length);
  const normalized = remainder.startsWith('/') ? remainder : remainder ? `/${remainder}` : '';
  return normalized || '/';
}

export function getCurrentStateId() {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(/^\/([a-zA-Z]{2})(?:\/|$)/);
  if (match) {
    const code = normalizeStateId(match[1]);
    if (code) {
      sessionStorage.setItem(STATE_STORAGE_KEY, code);
      return code;
    }
  }
  const stored = sessionStorage.getItem(STATE_STORAGE_KEY);
  const normalized = normalizeStateId(stored);
  if (!normalized && stored) {
    sessionStorage.removeItem(STATE_STORAGE_KEY);
  }
  return normalized;
}

export function persistStateId(stateId) {
  if (typeof window === 'undefined') return;
  const normalized = normalizeStateId(stateId);
  if (!normalized) return;
  sessionStorage.setItem(STATE_STORAGE_KEY, normalized);
}

export function getStatelessPathname() {
  if (typeof window === 'undefined') return '/';
  return stripStatePrefix(window.location.pathname || '/');
}

export function buildStateAwarePath(path = '/') {
  return path.startsWith('/') ? path : `/${path}`;
}

export function buildAppPath(path = '/') {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return stripStatePrefix(normalized);
}

export function buildPathForState(_stateId, path = '/') {
  return path.startsWith('/') ? path : `/${path}`;
}

const DEFAULT_ORG_SLUG = 'default';
const AUTH_PREFIX = 'mira_auth';

/**
 * Normalize a hostname into the expected org slug used throughout the app.
 */
export function getOrgSlugFromHost(hostname) {
  const { branchSlug, clientSlug } = parseHostname(hostname);
  return branchSlug || clientSlug || DEFAULT_ORG_SLUG;
}

/**
 * Resolve the org slug for the current browser environment.
 */
export function getCurrentOrgSlug() {
  const branchSlug = getBranchSlugFromHost();
  if (branchSlug) {
    return branchSlug;
  }
  const clientSlug = getCurrentClientSlug();
  return clientSlug || DEFAULT_ORG_SLUG;
}

export function getCurrentBranchSlug() {
  return getBranchSlugFromHost() || null;
}

export function getCurrentParentSlug() {
  return getParentSlugFromHost() || null;
}

const buildTenantSuffix = (clientSlug, branchSlug) => {
  const normalizedClient = clientSlug || DEFAULT_ORG_SLUG;
  return branchSlug ? `${normalizedClient}::${branchSlug}` : normalizedClient;
};

/**
 * Build a tenant-scoped storage key with the standard auth prefix.
 * Accepts a string slug, an options object, or defaults to the current host context.
 */
export function getTenantScopedStorageKey(baseKey, clientOrContext, maybeBranch) {
  let clientSlug = clientOrContext;
  let branchSlug = maybeBranch;

  if (typeof clientOrContext === 'object' && clientOrContext !== null) {
    clientSlug = clientOrContext.clientSlug || clientOrContext.orgSlug || clientOrContext.organizationSlug;
    branchSlug = clientOrContext.branchSlug || clientOrContext.branch;
  }

  if (typeof clientSlug === 'undefined' || clientSlug === null) {
    clientSlug = getCurrentClientSlug() || DEFAULT_ORG_SLUG;
  }

  if (typeof branchSlug === 'undefined') {
    branchSlug = getBranchSlugFromHost();
  }

  return `${AUTH_PREFIX}:${baseKey}:${buildTenantSuffix(clientSlug, branchSlug)}`;
}

/**
 * Build the tenant-specific session indicator key.
 */
export function getTenantSessionIndicatorKey(clientOrContext, maybeBranch) {
  let clientSlug = clientOrContext;
  let branchSlug = maybeBranch;

  if (typeof clientOrContext === 'object' && clientOrContext !== null) {
    clientSlug = clientOrContext.clientSlug || clientOrContext.orgSlug || clientOrContext.organizationSlug;
    branchSlug = clientOrContext.branchSlug || clientOrContext.branch;
  }

  if (typeof clientSlug === 'undefined' || clientSlug === null) {
    clientSlug = getCurrentClientSlug() || DEFAULT_ORG_SLUG;
  }

  if (typeof branchSlug === 'undefined') {
    branchSlug = getCurrentBranchSlug();
  }

  return `has_session:${buildTenantSuffix(clientSlug, branchSlug)}`;
}

export { DEFAULT_ORG_SLUG, AUTH_PREFIX, buildTenantSuffix, getCurrentClientSlug };

export function getCachedOrganization(orgSlug = getCurrentOrgSlug()) {
  if (typeof window === 'undefined') return null;
  if (!orgSlug) return null;
  try {
    const storageKey = getTenantScopedStorageKey('organization', orgSlug);
    const cached = localStorage.getItem(storageKey);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.warn('[tenant] Failed to parse cached organization', error);
    return null;
  }
}

export function getLicensedStatesFromCache(orgSlug = getCurrentOrgSlug()) {
  const org = getCachedOrganization(orgSlug);
  if (!org) return [];
  const states = Array.isArray(org.licensed_states) ? org.licensed_states : [];
  return states
    .map((state) => (state ? String(state).toUpperCase().trim() : ''))
    .filter(Boolean);
}
